import { Injectable } from "@angular/core";
import { collection, doc, Firestore, getDoc, getDocs, limit, orderBy, query, where } from "@angular/fire/firestore";
import { Data } from "@angular/router";
import { AuthService } from '@core/auth.service';
import { AuthStateService } from "@core/auth-state.service";
import { catchError, EMPTY, filter, from, map, Observable, of, shareReplay, switchMap, take, tap, throwError } from "rxjs";
import { HomeRepositoryService } from "src/app/pages/home/home-repository.service";
import { CommonService } from "src/app/services/common/common.service";
import { FirestoreDataService } from "src/app/services/firestore-data.service";

@Injectable({
  providedIn: "root",
})
export class HomecardsService {
  constructor(
    private firestoreDataService: FirestoreDataService,
    private firestore: Firestore,
    private authService: AuthService,
    private commonService: CommonService,
    private homeService: HomeRepositoryService,
    private authStateService: AuthStateService
  ) { }
  updateGamePromotion(userId: string) {
    this.firestoreDataService.getV2CloudFunction("updategamepromotionv2", userId).subscribe();
  }

  setGamePlayRestriction(userId: string) {
    this.firestoreDataService.getV2CloudFunction("setgameplayrestrictionv2", userId).subscribe();
  }

  processLoginGift(userId: string) {
    try {
      this.firestoreDataService.getV2CloudFunction("processlogingiftv2", userId).subscribe();
    } catch (error) {
      throw new Error("Error in processLoginGift");
    }
  }

  public customOfferCreditCheck(userid, award_id) {
    try {
      return this.firestoreDataService.getV2CloudFunction("customerofferpagev2", {
        userID: userid,
        award_id: award_id,
      });
    } catch (error) {
      return error;
    }
  }

  public getGlobalPartnerOffers(type: string): Observable<Data> {
    try {
      return this.firestoreDataService
        .getAngularFireStore(`/Global_lookup/partners/list/${type}`, "snapshotChanges")
        .pipe(
          map((res: Data) => res?.payload?.data()),
          catchError(() => of(null)),
          shareReplay(1)
        );
    } catch (error) {
      return throwError(error);
    }
  }

  // This function is used to get latest ongoing syndicate to take payment holiday
  async getLatestOngoingSyndicate(): Promise<any> {
    try {
      return this.authService.getUid$("SyndicateRepositoryService").pipe(
        switchMap(async (userId) => {
          if (!userId) userId = null;
          if (userId) {
            const managedTicketsRef = collection(this.firestore, `/users/${userId}/managed_tickets`);
            const ongoingSyndicateQuery = query(
              managedTicketsRef,
              where("syndicate_type", "==", "Ongoing"),
              orderBy("creation_date", "desc"),
              limit(1)
            );

            const querySnapshot = await getDocs(ongoingSyndicateQuery);

            if (!querySnapshot.empty) {
              const latestSyndicate = querySnapshot.docs[0].data();
              return latestSyndicate;
            } else {
              return null; // No ongoing syndicate found
            }
          }
          return of(userId) || of(null);
        }));

    } catch (error) {
      console.error("Error fetching ongoing syndicate:", error);
      return null; // Error occurred
    }
  }

  // This method is used updateAddToHomeCardStatus

  updateAddToHomeCardStatus(updatedEventStatus?: { event: string; status: boolean }): void {
    // Gate on customTokenReady$ to prevent permission-denied errors on iOS/Safari
    // where the Firebase custom token may not yet be applied when this Firestore call starts.
    this.authStateService.customTokenReady$.pipe(
      take(1),
      switchMap(() => this.commonService.getUid("HomecardsService")),
      take(1),
      filter((userId) => !!userId),
      switchMap((userId) => {
        const userHomeCardRef = doc(this.firestore, `/users/${userId}/user_HomeCard/list`);
        return from(getDoc(userHomeCardRef)).pipe(
          map((docSnapshot) => ({ userId, docSnapshot }))
        );
      }),
      switchMap(({ userId, docSnapshot }) => {
        if (!docSnapshot.exists()) {
          return EMPTY;
        }

        if (!updatedEventStatus?.event) {
          return EMPTY;
        }

        if(updatedEventStatus.event === "add_home_screen") {
          this.authService.getV2CloudFunction("updatefirestoredoc", {
            path_id: "yzF1Ss5eiMqnh91zQMtv",
            doc_data: {
              "add_home_screen": true,
              feature_used: {
                add_home_screen: {
                  last_used: Date.now(),
                },
              },
            },
            
            method: "update"
          }).subscribe();
        }

        const data = docSnapshot.data();
        
        // Add defensive null/undefined checks before accessing nested properties
        if (!data || !data.new_member_checklist || !data.new_member_checklist.content) {
          console.warn("[updateAddToHomeCardStatus] ⚠️ new_member_checklist data structure is missing.");
          return EMPTY;
        }
        
        const checklist = data.new_member_checklist.content.checklist;

        if (updatedEventStatus && Array.isArray(checklist) && checklist.length > 0) {
          const existingItem = checklist.find((item: any) => item?.event === updatedEventStatus.event);
          // Skip redundant write/CF when this step is already complete (HATDEV-99547)
          if (existingItem?.status === true && updatedEventStatus.status === true) {
            return EMPTY;
          }

          const wasAlreadyComplete = checklist.every((item: any) => item?.status === true);

          const updatedChecklist = checklist.map((item: any) =>
            item.event === updatedEventStatus.event
              ? { ...item, status: updatedEventStatus.status }
              : item
          );

          return this.authService.getV2CloudFunction("updatefirestoredoc", {
            path_id: "RdSJ8Cffwlw0MZ9VJ8yZ",
            doc_data: {
              new_member_checklist: {
                content: {
                  checklist: updatedChecklist,
                },
              },
            },
            method: "update",
          }).pipe(
            tap(() => {
              // Re-run assign CF while in progress, or when transitioning to fully complete
              // (sets button_pulse). Skip when already complete before this write.
              if (!wasAlreadyComplete) {
                this.homeService?.newMemberChecklistCard(userId);
              }
              this.authService.updateChecklistTooltip(updatedChecklist, userId);
            })
          );
        } else {
          console.warn("[updateAddToHomeCardStatus] ⚠️ Checklist not found or invalid format.");
        }
        return EMPTY;
      })
    ).subscribe({
      error: (err) => {
        this.authService.errorHandlerLogs('Error in updateAddToHomeCardStatus', err);
      },
    });
  }



}
